import type {
  PublishingDocument,
  PublishingElement,
  PublishingImageElement,
  PublishingPage,
  PublishingShapeElement,
  PublishingTextElement,
} from '../../types/document';
import {
  resolveColorToken,
  resolveFontToken,
  resolvePublishingTheme,
} from '../../tokens';
import type { PublishingTheme } from '../../themes';

interface RenderContext2D {
  globalAlpha: number;
  fillStyle: string;
  strokeStyle: string;
  lineWidth: number;
  font: string;
  textBaseline: 'top';
  translate(x: number, y: number): void;
  rotate(angle: number): void;
  scale(x: number, y: number): void;
  save(): void;
  restore(): void;
  beginPath(): void;
  moveTo(x: number, y: number): void;
  arcTo(x1: number, y1: number, x2: number, y2: number, radius: number): void;
  closePath(): void;
  fill(): void;
  stroke(): void;
  clip(): void;
  fillRect(x: number, y: number, width: number, height: number): void;
  measureText(text: string): { width: number };
  fillText(text: string, x: number, y: number): void;
  ellipse(
    x: number,
    y: number,
    radiusX: number,
    radiusY: number,
    rotation: number,
    startAngle: number,
    endAngle: number,
  ): void;
  setLineDash(segments: number[]): void;
  lineTo(x: number, y: number): void;
  drawImage(...args: unknown[]): void;
}

export interface BrowserCanvasLike {
  width: number;
  height: number;
  style: { width: string; height: string };
  getContext(type: '2d'): RenderContext2D | null;
  toDataURL(type?: string): string;
}

interface BrowserImageLike {
  width: number;
  height: number;
  crossOrigin: string | null;
  src: string;
  onload: (() => void) | null;
  onerror: (() => void) | null;
}

function getBrowserDocument() {
  return globalThis as {
    document?: {
      createElement(tagName: 'canvas'): BrowserCanvasLike;
    };
  };
}

function getImageConstructor() {
  return globalThis as {
    Image?: new () => BrowserImageLike;
  };
}

function applyElementTransform(
  context: RenderContext2D,
  element: PublishingElement,
) {
  context.translate(
    element.x + element.width / 2,
    element.y + element.height / 2,
  );
  context.rotate((element.rotation * Math.PI) / 180);
  context.translate(-(element.width / 2), -(element.height / 2));
  context.globalAlpha = element.opacity;
}

function buildTextFont(element: PublishingTextElement, theme: PublishingTheme) {
  const weight = element.style.fontWeight ?? 400;
  const fontFamily = resolveFontToken(
    element.style.fontToken,
    theme,
    element.style.fontFamily,
  );
  return `${weight} ${element.style.fontSize}px ${fontFamily}`;
}

function transformTextValue(element: PublishingTextElement) {
  return element.style.textTransform === 'uppercase'
    ? element.text.toUpperCase()
    : element.text;
}

function wrapText(context: RenderContext2D, text: string, maxWidth: number) {
  const paragraphs = text.split('\n');
  const lines: string[] = [];

  for (const paragraph of paragraphs) {
    const words = paragraph.split(/\s+/).filter(Boolean);

    if (words.length === 0) {
      lines.push('');
      continue;
    }

    let currentLine = words[0] ?? '';

    for (const word of words.slice(1)) {
      const nextLine = `${currentLine} ${word}`;

      if (context.measureText(nextLine).width <= maxWidth) {
        currentLine = nextLine;
        continue;
      }

      lines.push(currentLine);
      currentLine = word;
    }

    lines.push(currentLine);
  }

  return lines;
}

function drawTextLine(
  context: RenderContext2D,
  text: string,
  x: number,
  y: number,
  letterSpacing: number,
) {
  if (letterSpacing === 0) {
    context.fillText(text, x, y);
    return;
  }

  let cursor = x;

  for (const character of text) {
    context.fillText(character, cursor, y);
    cursor += context.measureText(character).width + letterSpacing;
  }
}

function drawTextElement(
  context: RenderContext2D,
  element: PublishingTextElement,
  theme: PublishingTheme,
) {
  context.save();
  applyElementTransform(context, element);
  context.font = buildTextFont(element, theme);
  context.fillStyle = resolveColorToken(
    element.style.colorToken,
    theme,
    element.style.color,
  );
  context.textBaseline = 'top';

  const value = transformTextValue(element);
  const lineHeight = element.style.lineHeight ?? 1.2;
  const lineHeightPx = element.style.fontSize * lineHeight;
  const letterSpacing = element.style.letterSpacing ?? 0;
  const lines = wrapText(context, value, element.width);

  for (const [lineIndex, line] of lines.entries()) {
    const lineWidth =
      context.measureText(line).width + letterSpacing * line.length;
    const x =
      element.style.align === 'center'
        ? (element.width - lineWidth) / 2
        : element.style.align === 'right'
          ? element.width - lineWidth
          : 0;
    drawTextLine(context, line, x, lineIndex * lineHeightPx, letterSpacing);
  }

  context.restore();
}

function createRoundedRectPath(
  context: RenderContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number,
) {
  const safeRadius = Math.max(0, Math.min(radius, width / 2, height / 2));
  context.beginPath();
  context.moveTo(x + safeRadius, y);
  context.arcTo(x + width, y, x + width, y + height, safeRadius);
  context.arcTo(x + width, y + height, x, y + height, safeRadius);
  context.arcTo(x, y + height, x, y, safeRadius);
  context.arcTo(x, y, x + width, y, safeRadius);
  context.closePath();
}

function drawShapeElement(
  context: RenderContext2D,
  element: PublishingShapeElement,
  theme: PublishingTheme,
) {
  context.save();
  applyElementTransform(context, element);
  context.setLineDash(element.strokeStyle === 'dashed' ? [12, 10] : []);
  const radius = element.cornerRadius ?? 0;

  if (element.shapeType === 'circle') {
    context.beginPath();
    context.ellipse(
      element.width / 2,
      element.height / 2,
      element.width / 2,
      element.height / 2,
      0,
      0,
      Math.PI * 2,
    );
  } else if (element.shapeType === 'line') {
    context.beginPath();
    context.moveTo(0, element.height / 2);
    context.lineTo(element.width, element.height / 2);
  } else if (element.shapeType === 'arrow') {
    const centerY = element.height / 2;
    const headSize = Math.min(28, element.width / 4);
    context.beginPath();
    context.moveTo(0, centerY);
    context.lineTo(element.width - headSize, centerY);
    context.lineTo(element.width - headSize, centerY - headSize / 1.5);
    context.lineTo(element.width, centerY);
    context.lineTo(element.width - headSize, centerY + headSize / 1.5);
    context.lineTo(element.width - headSize, centerY);
  } else {
    createRoundedRectPath(context, 0, 0, element.width, element.height, radius);
  }

  if (element.fill) {
    context.fillStyle = resolveColorToken(
      element.fillToken,
      theme,
      element.fill,
    );
    context.fill();
  } else if (element.fillToken) {
    context.fillStyle = resolveColorToken(element.fillToken, theme);
    context.fill();
  }

  if (element.stroke && element.strokeWidth) {
    context.strokeStyle = resolveColorToken(
      element.strokeToken,
      theme,
      element.stroke,
    );
    context.lineWidth = element.strokeWidth;
    context.stroke();
  } else if (element.strokeToken && element.strokeWidth) {
    context.strokeStyle = resolveColorToken(element.strokeToken, theme);
    context.lineWidth = element.strokeWidth;
    context.stroke();
  }

  context.restore();
}

async function loadImage(url: string) {
  return new Promise<BrowserImageLike>((resolve, reject) => {
    const BrowserImage = getImageConstructor().Image;

    if (!BrowserImage) {
      reject(new Error('Image constructor unavailable.'));
      return;
    }

    const image = new BrowserImage();
    image.crossOrigin = 'anonymous';
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error(`Could not load image: ${url}`));
    image.src = url;
  });
}

function drawImageElement(
  context: RenderContext2D,
  element: PublishingImageElement,
  image: BrowserImageLike,
) {
  context.save();
  applyElementTransform(context, element);
  const radius = element.cornerRadius ?? 0;

  if (radius > 0) {
    createRoundedRectPath(context, 0, 0, element.width, element.height, radius);
    context.clip();
  }

  const imageRatio = image.width / image.height;
  const frameRatio = element.width / element.height;

  if (element.fitMode === 'fit') {
    let drawWidth = element.width;
    let drawHeight = element.height;

    if (imageRatio > frameRatio) {
      drawHeight = drawWidth / imageRatio;
    } else {
      drawWidth = drawHeight * imageRatio;
    }

    context.drawImage(
      image,
      (element.width - drawWidth) / 2,
      (element.height - drawHeight) / 2,
      drawWidth,
      drawHeight,
    );
  } else {
    let sourceWidth = image.width;
    let sourceHeight = image.height;
    let sourceX = 0;
    let sourceY = 0;

    if (imageRatio > frameRatio) {
      sourceWidth = image.height * frameRatio;
      sourceX = (image.width - sourceWidth) / 2;
    } else {
      sourceHeight = image.width / frameRatio;
      sourceY = (image.height - sourceHeight) / 2;
    }

    context.drawImage(
      image,
      sourceX,
      sourceY,
      sourceWidth,
      sourceHeight,
      0,
      0,
      element.width,
      element.height,
    );
  }

  context.restore();
}

export async function renderPublishingPageToCanvas(input: {
  page: PublishingPage;
  documentMetadata?: PublishingDocument['metadata'];
  occasionTheme?: PublishingTheme | null;
  pixelRatio?: number;
  transparentBackground?: boolean;
  resolveImageUrl?: (assetId: string) => string | null | undefined;
  imageUrlByAssetId?: Record<string, string | null | undefined>;
}) {
  const browserDocument = getBrowserDocument().document;

  if (!browserDocument) {
    throw new Error('Browser document unavailable for rendering.');
  }

  const canvas = browserDocument.createElement('canvas');
  const pixelRatio = input.pixelRatio ?? 1;
  canvas.width = Math.max(1, Math.round(input.page.width * pixelRatio));
  canvas.height = Math.max(1, Math.round(input.page.height * pixelRatio));
  canvas.style.width = `${input.page.width}px`;
  canvas.style.height = `${input.page.height}px`;
  const context = canvas.getContext('2d');

  if (!context) {
    throw new Error('Canvas rendering context unavailable.');
  }

  context.scale(pixelRatio, pixelRatio);
  const theme = resolvePublishingTheme({
    occasionTheme: input.occasionTheme,
    documentThemeOverrides: input.documentMetadata?.themeOverrides,
    useThemeDefaults: input.documentMetadata?.useThemeDefaults,
  });

  if (!input.transparentBackground) {
    context.fillStyle = resolveColorToken(
      input.page.backgroundToken,
      theme,
      input.page.backgroundColor ?? '#ffffff',
    );
    context.fillRect(0, 0, input.page.width, input.page.height);
  }

  for (const element of input.page.elements) {
    if (element.type === 'text') {
      drawTextElement(context, element, theme);
      continue;
    }

    if (element.type === 'shape') {
      drawShapeElement(context, element, theme);
      continue;
    }

    const imageUrl =
      input.resolveImageUrl?.(element.assetId) ??
      input.imageUrlByAssetId?.[element.assetId] ??
      null;

    if (!imageUrl) {
      continue;
    }

    const image = await loadImage(imageUrl);
    drawImageElement(context, element, image);
  }

  return canvas;
}

export async function renderPublishingDocumentPages(input: {
  document: Pick<PublishingDocument, 'pages' | 'metadata' | 'resolvedTheme'>;
  occasionTheme?: PublishingTheme | null;
  pixelRatio?: number;
  transparentBackground?: boolean;
  resolveImageUrl?: (assetId: string) => string | null | undefined;
  imageUrlByAssetId?: Record<string, string | null | undefined>;
}) {
  const canvases: BrowserCanvasLike[] = [];

  for (const page of input.document.pages) {
    canvases.push(
      await renderPublishingPageToCanvas({
        page,
        documentMetadata: input.document.metadata,
        occasionTheme: input.occasionTheme ?? input.document.resolvedTheme,
        pixelRatio: input.pixelRatio,
        transparentBackground: input.transparentBackground,
        resolveImageUrl: input.resolveImageUrl,
        imageUrlByAssetId: input.imageUrlByAssetId,
      }),
    );
  }

  return canvases;
}
